import authService from "../api/auth/authService";
import tokenStore from "./tokenStore";
import { postSessionMessage } from "./sessionChannel";

// THE single-flight refresh lock, shared by the axios interceptor AND the
// session-expiry modal's Stay Logged In. The backend ROTATES the refresh token
// on every call, so concurrent refreshes must collapse into one: the first
// caller performs it, everyone else awaits the same promise.
//
// Cross-tab: if another tab refreshed within the last few seconds
// (localStorage last-refresh-at + TOKEN_REFRESHED broadcast), skip the network
// call and reuse the cookie it wrote. Residual races are absorbed server-side
// by the rotation grace window.

const CROSS_TAB_REUSE_MS = 5000;

let inFlight: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  const data = await authService.refreshToken();
  if (data && data.status && data.token && data.token.access_token) {
    tokenStore.setTokens(data.token, data);
    postSessionMessage({ type: "TOKEN_REFRESHED" });
    const header = tokenStore.getAuthHeader();
    if (header) return header;
  }
  // makeApiCall swallows axios errors and returns them, so a failed refresh
  // usually lands here as a non-token object rather than a throw.
  throw new Error(data?.message || "Token refresh failed");
}

// Resolves with the new Authorization header value; rejects when the refresh
// token itself is invalid/expired (caller decides to log out).
export function refreshSession(): Promise<string> {
  if (inFlight) return inFlight;

  const lastRefreshAt = tokenStore.getLastRefreshAt();
  if (lastRefreshAt && Date.now() - lastRefreshAt < CROSS_TAB_REUSE_MS) {
    const header = tokenStore.getAuthHeader();
    if (header) return Promise.resolve(header);
  }

  inFlight = doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
